/**
 * Lightweight in-memory cache for dashboard API responses.
 * Deduplicates in-flight requests and supports TTL + explicit invalidation.
 */

const DEFAULT_TTL_MS = 60_000;
const CALENDAR_TTL_MS = 120_000;

/** @type {Map<string, { data: unknown, timestamp: number, ttl: number }>} */
const cacheStore = new Map();

/** @type {Map<string, Promise<unknown>>} */
const inflightStore = new Map();

function isFresh(entry) {
  if (!entry) return false;
  return Date.now() - entry.timestamp <= entry.ttl;
}

function keyMatchesPrefix(key, prefix) {
  const normalized = String(prefix || '');
  return !normalized || key.startsWith(normalized);
}

export function readDashboardCache(key) {
  const entry = cacheStore.get(key);
  return isFresh(entry) ? entry.data : null;
}

export function invalidateDashboardDataCache(prefix = '') {
  for (const key of [...cacheStore.keys()]) {
    if (keyMatchesPrefix(key, prefix)) {
      cacheStore.delete(key);
    }
  }
  for (const key of [...inflightStore.keys()]) {
    if (keyMatchesPrefix(key, prefix)) {
      inflightStore.delete(key);
    }
  }
}

/**
 * Fetch with deduplication and TTL caching.
 * @param {string} key
 * @param {() => Promise<T>} fetcher
 * @param {{ ttl?: number, forceRefresh?: boolean }} [options]
 * @returns {Promise<T>}
 */
export async function fetchWithDashboardCache(key, fetcher, options = {}) {
  const { ttl = DEFAULT_TTL_MS, forceRefresh = false } = options;

  if (!forceRefresh) {
    const cached = cacheStore.get(key);
    if (isFresh(cached)) {
      return cached.data;
    }
  } else {
    cacheStore.delete(key);
    inflightStore.delete(key);
  }

  if (!forceRefresh) {
    const inflight = inflightStore.get(key);
    if (inflight) {
      return inflight;
    }
  }

  const promise = (async () => {
    try {
      const data = await fetcher();
      cacheStore.set(key, { data, timestamp: Date.now(), ttl });
      return data;
    } catch (error) {
      cacheStore.delete(key);
      throw error;
    } finally {
      inflightStore.delete(key);
    }
  })();

  inflightStore.set(key, promise);
  return promise;
}

export const DASHBOARD_PENDING_TASKS_TTL_MS = DEFAULT_TTL_MS;
export const DASHBOARD_CALENDAR_CACHE_TTL_MS = CALENDAR_TTL_MS;

export function getDashboardPendingTasksCacheKey(dateStr) {
  const hotelId = window.DJANGO_CONTEXT?.selectedHotel?.id ?? 'none';
  return `pending-tasks:${hotelId}:${dateStr}`;
}

export function getDashboardCalendarCacheKey(startDate, endDate, viewType) {
  const hotelId = window.DJANGO_CONTEXT?.selectedHotel?.id ?? 'none';
  return `calendar:${hotelId}:${startDate}:${endDate}:${viewType}`;
}

export function getOrganizationStatsCacheKey(hotelId, startDate, endDate) {
  return `org-stats:${hotelId}:${startDate}:${endDate}`;
}
